import React, { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  CssBaseline,
  Box,
  useMediaQuery,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Firebase
import { auth } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

// Layout Components
import Login from "./auth/Login";
import Navbar from "./layout/Navbar";
import Sidebar from "./layout/Sidebar";
import MainContent from "./layout/Maincontent";

// Pages
import Market from "./pages/Market";
import Videos from "./pages/Videos";
import Articles from "./pages/Articles";
import Charcha from "./pages/Charcha";
import Products from "./pages/Products";
import CowshedAnimals from "./pages/CowshedAnimals";
import OrderHistory from "./pages/OrderHistory";
import FAQs from "./pages/FAQs";
import HelpSupport from "./pages/HelpSupport";

import colors from "./constants/colors";
import Banners from "./pages/Banners";
import OwnBanners from "./pages/OwnBanners";
import DeliveryCharges from "./pages/DeliveryCharges";
import AllUsers from "./pages/AllUsers";
const theme = createTheme({
  palette: {
    primary: { main: colors.green },
    secondary: { main: colors.orange },
    background: { default: colors.lightGray },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
    },
  },
});

// Improved user role check function
const checkUserRoleByEmail = async (email) => {
  try {
    console.log("Checking user role for:", email);

    // Use absolute URL to avoid path issues
    const API_URL = process.env.REACT_APP_API_URL || "https://api.okmarket.cloud";
    const response = await fetch(
      `${API_URL}/api/users/role/${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }
    );

    console.log("Response status:", response.status);

    // First check if response is JSON
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      // If not JSON, read as text to see what we got
      const text = await response.text();
      console.error(
        "Server returned non-JSON response:",
        text.substring(0, 200)
      );

      // If it's HTML, likely a 404 or server error
      if (
        text.trim().startsWith("<!DOCTYPE") ||
        text.trim().startsWith("<html")
      ) {
        throw new Error("Server endpoint not found. Please check the API URL.");
      }

      throw new Error(`Unexpected response format: ${contentType}`);
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    const data = await response.json();
    console.log("Role check successful:", data);
    return data;
  } catch (error) {
    console.error("Error checking user role:", error);
    throw error;
  }
};

// Fallback function to create user data when API fails
const createFallbackUserData = (firebaseUser) => {
  return {
    name:
      firebaseUser.displayName ||
      firebaseUser.email.split("@")[0] ||
      "Admin User",
    email: firebaseUser.email,
    role: "admin", // Default to admin for fallback
    photoURL: firebaseUser.photoURL || null,
  };
};

// Main App Content Component
const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isMobile = useMediaQuery("(max-width:800px)");

  // Responsive sidebar handling
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // 🔁 Firebase Auth State Observer for Session Management
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          console.log("Firebase user detected:", firebaseUser.email);

          // For now, let's skip the API check and use fallback data
          // This will show the email immediately while we fix the API
          try {
            // Try the API check but don't block on failure
            const result = await checkUserRoleByEmail(firebaseUser.email);

            if (result.success && result.role === "admin") {
              console.log("User is admin via API");
              setUser({
                email: firebaseUser.email,
                role: result.role,
                userData: result.data || createFallbackUserData(firebaseUser),
                firebaseUser: firebaseUser,
              });
            } else {
              console.log("User is not admin via API");
              await handleLogout();
            }
          } catch (apiError) {
            console.warn(
              "API check failed, using fallback data:",
              apiError.message
            );
            // Use fallback data when API fails
            setUser({
              email: firebaseUser.email,
              role: "admin",
              userData: createFallbackUserData(firebaseUser),
              firebaseUser: firebaseUser,
            });
          }
        } else {
          console.log("No Firebase user found");
          setUser(null);
        }
      } catch (error) {
        console.error("Error in auth state change:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // ✅ Handle successful login
  const handleLogin = (userData) => {
    console.log("Login successful:", userData);
    setUser(userData);
    setLoginModalOpen(false);
  };

  // 🚪 Handle logout
  const handleLogout = async () => {
    try {
      console.log("Logging out user");
      await signOut(auth);
      setUser(null);
      setSearchText("");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // 🧭 Sidebar toggle
  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // 🔍 Clear search function
  const clearSearch = () => {
    setSearchText("");
  };

  // 🔓 Handle login modal
  const handleLoginModalOpen = () => {
    setLoginModalOpen(true);
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
  };

  // Handle login navigation from sidebar
  const handleLoginNavigation = () => {
    setLoginModalOpen(true);
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: `linear-gradient(135deg, ${colors.lightMint} 0%, ${colors.white} 100%)`,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={60}
            sx={{
              color: colors.green,
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: colors.darkGreen,
              fontWeight: 500,
            }}
          >
            Loading....
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: colors.lightGray,
      }}
    >
      {/* 🟢 Navbar - Always visible */}
      <Navbar
        user={user?.userData}
        onLogout={handleLogout}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
        onSearchChange={setSearchText}
        searchText={searchText}
        onLoginClick={handleLoginModalOpen}
        isMobile={isMobile}
      />

      {/* 🟠 Sidebar - Conditionally rendered based on screen size */}
      <Sidebar
        open={sidebarOpen}
        onToggle={toggleSidebar}
        user={user}
        onLoginRequired={handleLoginNavigation}
        isMobile={isMobile}
      />

      {/* 🟣 Main Content Area */}
      <MainContent sidebarOpen={!isMobile && sidebarOpen}>
        <Routes>
          {/* Default route redirects to market */}
          <Route path="/" element={<Navigate to="/market" replace />} />

          <Route
            path="/banners"
            element={
              user ? (
                <Banners adminData={user.userData} />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/own-banners"
            element={
              user ? (
                <OwnBanners adminData={user.userData} />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/delivery-charges"
            element={
              user ? (
                <DeliveryCharges adminData={user.userData} />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          {/* 🛒 Market - Public route (always accessible) */}
          <Route
            path="/market"
            element={
              <Market
                searchText={searchText}
                onClearSearch={clearSearch}
                user={user}
              />
            }
          />

          {/* 🔒 Protected Routes - Only accessible when logged in */}
          <Route
            path="/videos"
            element={
              user ? (
                <Videos
                  adminData={user.userData}
                  searchText={searchText}
                  onClearSearch={clearSearch}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/articles"
            element={
              user ? (
                <Articles
                  adminData={user.userData}
                  searchText={searchText}
                  onClearSearch={clearSearch}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/charcha"
            element={
              user ? (
                <Charcha adminData={user.userData} />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/products"
            element={
              user ? (
                <Products
                  adminData={user.userData}
                  searchText={searchText}
                  onClearSearch={clearSearch}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/cowshed-animals"
            element={
              user ? (
                <CowshedAnimals
                  adminData={user.userData}
                  searchText={searchText}
                  onClearSearch={clearSearch}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/order-history"
            element={
              user ? (
                <OrderHistory
                  adminData={user.userData}
                  searchText={searchText}
                  onClearSearch={clearSearch}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          <Route
            path="/all-users"
            element={
              user ? (
                <AllUsers
                  adminData={user.userData}
                  searchText={searchText}
                />
              ) : (
                <Navigate to="/market" replace />
              )
            }
          />

          {/* 📚 Public Routes */}
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/support" element={<HelpSupport />} />

          {/* Catch all route - redirect to market */}
          <Route path="*" element={<Navigate to="/market" replace />} />
        </Routes>
      </MainContent>

      {/* 🔐 Login Modal - Shows when login is requested */}
      {loginModalOpen && (
        <Login
          onLogin={handleLogin}
          onClose={handleLoginModalClose}
          isModal={true}
        />
      )}
    </Box>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
